import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { Palette, Theme } from '@mui/material';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { dicomColorToVec3 } from './color';

/**
 * Converts RGB values (0-1 range) to CSS rgb string
 */
export function rgbToString(r: number, g: number, b: number): string {
    return `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`;
}

/**
 * Converts a normalized RGB vec3 ([0–1] per channel)
 * to a CSS hex color string (e.g. '#00ffff').
 */
export function vec3ToHex(v: ReadonlyVec3): string {
    const r = Math.round(v[0] * 255);
    const g = Math.round(v[1] * 255);
    const b = Math.round(v[2] * 255);
    return (
        '#' +
        r.toString(16).padStart(2, '0') +
        g.toString(16).padStart(2, '0') +
        b.toString(16).padStart(2, '0')
    );
}

/** An 8-bit colour as an upper-case `#RRGGBB` label. */
export function dicomColorToHex(color: DicomColor): string {
    return vec3ToHex(dicomColorToVec3(color)).toUpperCase();
}

/** Whether two 8-bit colours are the same displayed colour. */
export function dicomColorsEqual(a: DicomColor, b: DicomColor): boolean {
    return a.R === b.R && a.G === b.G && a.B === b.B;
}

/**
 * Parses a hex colour — `#RGB` or `#RRGGBB`, with or without the `#`, in
 * any case, ignoring surrounding whitespace — into 8-bit channels.
 * Returns `null` for anything else, including partial input such as
 * `#12`, so a caller can keep its last valid value while the user types.
 */
export function parseHexColor(input: string): DicomColor | null {
    const hex = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(input.trim())?.[1];
    if (!hex) {
        return null;
    }
    const [R, G, B] = (hex.length === 3 ? hex.replace(/./g, '$&$&') : hex)
        .match(/../g)!
        .map((pair) => parseInt(pair, 16));
    return { R, G, B };
}

/**
 * Converts hex or rgb color to rgba with alpha channel
 */
export function toRgba(color: string, alpha: number): string {
    if (typeof color !== 'string') {
        return color;
    }

    // Handle hex colors
    if (color.startsWith('#')) {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    // Handle rgb/rgba colors
    if (color.startsWith('rgb')) {
        const match = color.match(/\d+/g);
        if (match && match.length >= 3) {
            return `rgba(${match[0]}, ${match[1]}, ${match[2]}, ${alpha})`;
        }
    }

    return color;
}

/**
 * Gets the actual color value from MUI theme palette
 */
export function resolveThemeColor(colorValue: string, theme: Theme): string {
    const paletteKey = colorValue.split('.')[0];
    const paletteColor = theme.palette[paletteKey as keyof Palette];

    if (
        paletteColor &&
        typeof paletteColor === 'object' &&
        'main' in paletteColor
    ) {
        return (paletteColor as { main: string }).main;
    }

    return colorValue;
}

/**
 * Lightens a color by mixing it with white to make it more suitable
 * for preview overlays. The blend factor determines how much white
 * is mixed in (0 = original color, 1 = white).
 */
export function lightenColor(color: ReadonlyVec3, blendFactor = 0.4): vec3 {
    const r = color[0] * (1 - blendFactor) + blendFactor;
    const g = color[1] * (1 - blendFactor) + blendFactor;
    const b = color[2] * (1 - blendFactor) + blendFactor;
    return vec3.fromValues(r, g, b);
}
